import { XCircleIcon } from '@heroicons/react/24/outline';
import debug from 'debug';
import * as React from 'react';
import { Constants } from '../../../../../Constants';
import { useChat, useSites } from '../../../../../libs/hooks';
import { ISiteInfo } from '../../../../../libs/models/SelectedSites';
import CacheUtils from '../../../../../libs/utils/CacheUtils';
import { useAppDispatch, useAppSelector } from '../../../../../redux/app/hooks';
import { RootState } from '../../../../../redux/app/store';
import { setNotifications } from '../../../../../redux/features/app/appSlice';
import { editConversationSiteSource } from '../../../../../redux/features/conversations/conversationsSlice';
import { Icons } from '../../../../Icons/Icons';
import { NOTIFICATION_TEMPLATES } from '../../Notifications/NotificationConstants';
import { Notifications } from '../../Notifications/Notifications';
import { LoadingMessage } from '../Shared/LoadingMessage';
import { Table, TableRowProps } from '../Shared/Table';

const log = debug(Constants.debug.root).extend('sites-tab');

export const SitesTab: React.FC = () => {
    const [indexedSites, setIndexedSites] = React.useState<ISiteInfo[]>([]);
    const [selectedSites, setSelectedSites] = React.useState<ISiteInfo[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [isSaving, setIsSaving] = React.useState(false);
    const [hasChanged, setHasChanged] = React.useState(false);
    const sitesService = useSites();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo, notifications, isChatNotificationsEnabled } = useAppSelector(
        (state: RootState) => state.app,
    );
    const dataCacheKey = `SitesTab.GetSites.${activeUserInfo?.id}`;

    const chatState = conversations[selectedId];

    const dispatch = useAppDispatch();
    const chat = useChat();

    React.useEffect(() => {
        const cachedData: ISiteInfo[] | null = CacheUtils.getStoredDataByKey(dataCacheKey, true);
        if (cachedData !== null && cachedData.length > 0) {
            setIndexedSites(cachedData);
            setLoading(false);
            return;
        }
        sitesService
            .getSites()
            .then((results: ISiteInfo[]) => {
                const items: ISiteInfo[] = [];
                results.forEach((site: ISiteInfo) => {
                    items.push({
                        siteName: site.siteName,
                        siteUrl: site.siteUrl,
                        siteId: site.siteId,
                    });
                });
                setLoading(false);
                CacheUtils.setStoredDataByKey(dataCacheKey, items, CacheUtils.dataCacheExpires24Hours, true);
                setIndexedSites(items);
            })
            .catch((error) => {
                const message = `Error submitting chat input: ${(error as Error).message}`;
                log(message);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        setIsSaving(false);
        if (!chatState.selectedSites || chatState.selectedSites.length === 0) return;
        const currentSelectedSites = chatState.selectedSites;
        setSelectedSites(currentSelectedSites);
    }, [chatState.selectedSites]);

    const onSelectionCheckboxChange = (tableRows: TableRowProps[]) => {
        setHasChanged(true);
        const selectedTableRows = tableRows.filter((row) => row.checked);
        // Map selected table rows to ISiteInfo
        setSelectedSites(indexedSites.filter((site) => selectedTableRows.some((row) => row.id === site.siteId)));
    };

    const updateConversationSiteSource = async (updatedSelectedSites: ISiteInfo[]) => {
        if (chatState.isSaved) {
            await chat.editChat(
                selectedId,
                chatState.title,
                chatState.memoryBalance,
                chatState.chatScope,
                updatedSelectedSites,
                chatState.hidden,
                chatState.authoritiveDocuments,
            );
        }

        dispatch(
            editConversationSiteSource({
                id: selectedId,
                siteSources: updatedSelectedSites,
            }),
        );
    };

    const onSavePreference = () => {
        setIsSaving(true);
        setSelectedSites(selectedSites);
        updateConversationSiteSource(selectedSites).catch((error) => {
            log('An error occured whilst editing chat state', error);
        });
        dispatch(setNotifications([{ ...NOTIFICATION_TEMPLATES.INDEXED_SOURCES_PREFERENCES }]));
        setHasChanged(false);
    };

    const onRemoveSelection = (selectedSiteId: string) => {
        const updatedSelectedSites: ISiteInfo[] = selectedSites.filter((site) => site.siteId !== selectedSiteId);
        updateConversationSiteSource(updatedSelectedSites).catch((error) => {
            log('An error occured whilst editing chat state', error);
        });
        setSelectedSites(updatedSelectedSites);
    };

    const saveButton: JSX.Element = (
        <div className="flex">
            <button
                className={`inline-flex justify-center shadow-md rounded-lg p-2 text-white dark:text-slate-50 transition-all duration-200 ${hasChanged && !isSaving ? 'bg-primary-300 hover:bg-primary-700 cursor-pointer' : 'bg-gray-400 hover:bg-gray-400'}`}
                onClick={onSavePreference}
                disabled={!hasChanged || isSaving}
                title="Save preference">
                {isSaving ? (
                    <svg
                        aria-hidden="true"
                        className="inline w-5 h-5 mr-1 text-gray-200 animate-spin dark:text-gray-600 fill-primary-300"
                        viewBox="0 0 100 101"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor"
                        />
                        <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill"
                        />
                    </svg>
                ) : (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.25"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        className="h-5 w-5">
                        <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
                        <path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" />
                        <path d="M7 3v4a1 1 0 0 0 1 1h7" />
                    </svg>
                )}
                <span className="px-2 text-sm">Save preference</span>
            </button>
        </div>
    );

    return (
        <div className="flex-1 overflow-y-auto bg-slate-100 leading-6 shadow-md dark:bg-slate-900">
            <div className="m-6">
                {loading ? (
                    <LoadingMessage message="Fetching indexed sources..." />
                ) : (
                    <>
                        <div className="flex flex-col my-3 mx-0">
                            <h1 className="mb-3 text-2xl">Indexed Sources</h1>
                            {selectedSites.length > 0 && (
                                <div className="flex flex-col mb-3">
                                    <p className="mb-3 text-sm">{`Currently the following selected source${selectedSites.length > 1 ? 's' : ''} are being considered for your responses:`}</p>
                                    <div className="flex">
                                        <div className="flex flex-wrap items-center flex-row">
                                            {selectedSites.map((site) => (
                                                <div
                                                    key={`site-${site.siteId}`}
                                                    className="min-h-9 items-center mr-1 mb-1 bg-primary-300 text-slate-50 dark:bg-primary-400 dark:text-slate-300 rounded-md pl-3 dark:text-primary flex">
                                                    <div className="flex mr-2 min-w-5 size-5 items-center justify-center">
                                                        <Icons.sharepoint />
                                                    </div>
                                                    <div className="text-md pr-2">{site.siteName}</div>
                                                    <div
                                                        className="cursor-pointer hover:text-red-500"
                                                        onClick={() => {
                                                            onRemoveSelection(site.siteId);
                                                        }}>
                                                        <XCircleIcon
                                                            className="text-slate-100 dark:text-slate-200 mr-2 h-5 w-5 hover:text-lavender-dark dark:hover:text-white transition-all"
                                                            strokeWidth="2"
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="text-sm mb-3">
                                The table below shows a listing of the indexed SharePoint / Teams sources that are
                                available for this app. You can select specific sources to be used as the source of the
                                chat (all sources are selected by default):
                            </div>

                            {indexedSites.length === 0 && <div>No sites found</div>}
                            {indexedSites.length > 0 && (
                                <>
                                    {saveButton}
                                    <Table
                                        data={{
                                            headings: ['Name', 'Url'],
                                            rows: indexedSites.map((site) => ({
                                                id: site.siteId,
                                                type: 'td',
                                                checkbox: 'enabled',
                                                checked: selectedSites.some(
                                                    (selectedSite) => selectedSite.siteId === site.siteId,
                                                ),
                                                siteName: site.siteName,
                                                siteUrl: (
                                                    <a
                                                        href={site.siteUrl}
                                                        title={site.siteUrl}
                                                        className="text-primary-400 hover:underline">
                                                        {site.siteUrl}
                                                    </a>
                                                ),
                                                siteId: site.siteId,
                                            })),
                                            hidden: ['type', 'checkbox', 'checked', 'id', 'siteId'],
                                        }}
                                        onRowsSelectionChange={onSelectionCheckboxChange}
                                        isSelectAllEnabled={true}
                                    />
                                </>
                            )}
                            {saveButton}
                        </div>
                    </>
                )}
            </div>
            {isChatNotificationsEnabled && notifications && notifications.length > 0 && (
                <Notifications messages={notifications} />
            )}
        </div>
    );
};
